import i18n from 'i18next';
import { v4 as uuidv4 } from 'uuid';
import {
  StringAnswer,
  ExpressionAnswer,
  IGameSession,
  IAnswerHint,
  GameSessionState,
  isNullOrUndefined,
  ConfidenceLevel,
  AnswerFactory,
  AnswerType,
  BackendAnswer
} from '@righton/networking';
import {
  InputPlaceholder,
  StorageKey,
  LocalModel,
  StorageKeyAnswer,
} from './PlayModels';
import { safeStorage } from './safeStorage';

/**
 * check if name entered isn't empty or the default value
 * TODO: add check for inappropriate words
 * @param name - the inputted string for the name
 * @returns - true if the name is valid, false otherwise
 */
export const isNameValid = (name: string) => {
  return (
    name.trim() !== '' && // check for falsy w/o spaces, typ
    name !== i18n.t('joingame.playername.firstnamedefault') && // check if default value, typ
    name !== i18n.t('joingame.playername.lastnamedefault')
  );
};

/**
 * check if gameCode is 4 characters, isn't empty and isn't the default value, is numeric
 * @param gameCode - the inputted string for the game code
 * @returns - true if the game code is valid, false otherwise
 */
export const isGameCodeValid = (gameCode: string) => {
  return (
    gameCode.trim() !== '' &&
    gameCode !== InputPlaceholder.GAME_CODE &&
    gameCode.length === 4 &&
    !Number.isNaN(parseInt(gameCode, 10))
  );
};

/**
 * on rejoining game, this checks if the player has already submitted an answer
 * @param localModel - the localModel retrieved from local storage
 * @param hasRejoined - if a player is rejoining
 * @param answers - the answers submitted by the player previously
 * @param answerChoices - the answer choices for the question on the backend
 * @param currentState - the current state of the game session
 * @returns - the index of the answer the player has submitted, null if they haven't submitted an answer and boolean to track submission
 */
export const checkForSubmittedAnswerOnRejoin = (
  localModel: LocalModel,
  hasRejoined: boolean,
  currentState: GameSessionState,
  currentQuestionIndex: number,
  isShortAnswerEnabled: boolean,
): BackendAnswer => {
  let returnedAnswer: BackendAnswer = {
    id: uuidv4(),
    answer: AnswerFactory.createAnswer('', AnswerType.STRING),
    isSubmitted: false,
    currentState: GameSessionState.TEAMS_JOINING,
    currentQuestionIndex: 0,
    isShortAnswerEnabled,
    questionId: '',
    teamMemberAnswersId: '',
    teamAnswersId: '',
    teamName: '',
    text: '',
    isCorrect: false,
  };
  if (hasRejoined) {
    if (
      localModel.answer !== null &&
      localModel.answer.currentState === currentState &&
      localModel.answer.currentQuestionIndex === currentQuestionIndex &&
      // Identity guard: only restore an answer that belongs to THIS game/team.
      // StorageKeyAnswer leaks across games, so a prior game's Q1 answer (same phase
      // + index) would otherwise be reattached as "submitted" and lock the answer UI.
      localModel.answer.teamMemberAnswersId === localModel.teamMemberAnswersId
    ) {
      // set answer to localAnswer
      returnedAnswer = localModel.answer;
      // remove localAnswer from local storage
      localModel.answer = null; // eslint-disable-line no-param-reassign
      safeStorage.setItem(StorageKey, JSON.stringify(localModel));
    }
  }
  return returnedAnswer as BackendAnswer;
};

/**
 * on rejoining game, this checks if the player has already submitted a hint
 * @param localModel - the localModel retrieved from local storage
 * @param hasRejoined - if a player is rejoining
 * @param currentState - the current state of the game session
 * @param currentQuestionIndex - the current question index of the game session
 * @returns - the hint that the player was working on, null if they haven't submitted a hint 
 */
export const checkForSubmittedHintOnRejoin = (
  localModel: LocalModel,
  hasRejoined: boolean,
  currentState: GameSessionState,
  currentQuestionIndex: number
): IAnswerHint => {
  let returnedHint: IAnswerHint = {
    rawHint: '',
    teamName: '',
    isHintSubmitted: false,
  };
  if (hasRejoined) {
    if (
      localModel.answer !== null &&
      !isNullOrUndefined(localModel.answer.hint) &&
      localModel.answer.currentState === currentState &&
      localModel.answer.currentQuestionIndex === currentQuestionIndex &&
      // Identity guard: only restore a hint that belongs to THIS game/team (see
      // checkForSubmittedAnswerOnRejoin — StorageKeyAnswer leaks across games).
      localModel.answer.teamMemberAnswersId === localModel.teamMemberAnswersId
    ) {
      // set hint to localModel.hint
      returnedHint = localModel.answer.hint;
    }
  }
  return returnedHint as IAnswerHint;
};

/**
 * on rejoining game, this checks if the player has already selected a confidence level
 * @param hasRejoined - if a player is rejoining
 * @param currentAnswer - the player's answer to the current question
 * @param currentState - the current state of the game session
 * @returns - the index of the confidence the player has submitted, null if they haven't selected a confidence, boolean to track submission
 */
export const checkForSelectedConfidenceOnRejoin = (
  hasRejoined: boolean,
  currentAnswer: BackendAnswer | null | undefined,
  currentState: GameSessionState
): {
  selectedConfidenceOption: string;
  isSelected: boolean;
} => {
  let selectedConfidenceOption = ConfidenceLevel.NOT_RATED;
  let isSelected = false;
  if (
    hasRejoined &&
    (currentState === GameSessionState.CHOOSE_CORRECT_ANSWER ||
      currentState === GameSessionState.CHOOSE_TRICKIEST_ANSWER) &&
    !isNullOrUndefined(currentAnswer) && !isNullOrUndefined(currentAnswer.confidenceLevel)
  ) {
    isSelected = currentAnswer.confidenceLevel !== ConfidenceLevel.NOT_RATED;
    selectedConfidenceOption = currentAnswer.confidenceLevel;
  }
  return { selectedConfidenceOption, isSelected };
};

/**
 * validates localModel retrieved from local storage
 * separate function to allow for ease of testing
 * @param localModelBase - the localModel retrieved from local storage
 * @returns - the localModel if valid, null otherwise
 */
export const validateLocalModel = (
  localModelBase: string | null,
  localModelAnswer: string | null
) => {
  if (isNullOrUndefined(localModelBase) || localModelBase === '') return null;
  const parsedLocalModel = JSON.parse(localModelBase);

  // checks for invalid data in localModel, returns null if found
  if (
    [
      parsedLocalModel.currentTime,
      parsedLocalModel.gameSessionId,
      parsedLocalModel.teamId,
      parsedLocalModel.teamMemberAnswersId,
      parsedLocalModel.selectedAvatar,
      parsedLocalModel.hasRejoined,
      parsedLocalModel.currentTimer,
    ].some((value) => isNullOrUndefined(value) || value === '')
  ) {
    return null;
  }

  const currentTime = new Date().getTime() / 60000;
  const elapsedTime = currentTime - parsedLocalModel.currentTime;

  // if the time between last accessing localModel and now is greater than 2 hours, remove localModel
  if (elapsedTime > 120) {
    return null;
  }
  if (!isNullOrUndefined(localModelAnswer) && localModelAnswer !== '') {
    const parsedLocalModelAnswer = JSON.parse(localModelAnswer);
    parsedLocalModel.localAnswer = parsedLocalModelAnswer;
  }
  // passes validated localModel to GameInProgressContainer
  return parsedLocalModel;
};

/**
 * retrieves local data from local storage and calls validator function
 * @returns - the localModel if valid, null otherwise
 */
export const fetchLocalData = () => {
  const localModel = validateLocalModel(
    safeStorage.getItem(StorageKey),
    safeStorage.getItem(StorageKeyAnswer)
  );
  if (!localModel) {
    safeStorage.removeItem(StorageKey);
    safeStorage.removeItem(StorageKeyAnswer);
  }
  return localModel;
};

/** 
  * calculates the current time by comparing the start time to Date.now
  * @param response - the game session response from the backend
  * @returns - the remaining time in seconds
  */
export const calculateCurrentTime = (response: IGameSession | null) => {
  let allottedTime = 0;
  console.log(response);
  if (response){
    if (response?.currentState === GameSessionState.CHOOSE_CORRECT_ANSWER) {
      allottedTime = response?.phaseOneTime;
    } else if (response?.currentState === GameSessionState.CHOOSE_TRICKIEST_ANSWER) {
      allottedTime = response?.phaseTwoTime;
    }

    const getStartTime = Number(response?.startTime);
    if (getStartTime) {
      const difference = Date.now() - getStartTime;
      if (difference >= allottedTime * 1000) {
        return 0;
      } 
      const remainingTime = allottedTime - Math.trunc(difference / 1000);
      console.log(remainingTime)
      return remainingTime;
    }
  }
  return 0;
};